/**
 * The one place `@packages/logger` touches tslog. The facade (types, logger, transports) is
 * engine-agnostic; swapping tslog for another engine means rewriting only this file (ADR 0036).
 *
 * Responsibilities:
 *  - construct the root tslog logger (level, `mask.*` redaction, console `type`, JSON key shape);
 *  - fan out every emitted record to the facade {@link Transport}s (Sentry, custom) by attaching a
 *    single tslog transport that parses the JSON-formatted line into our {@link LogRecord};
 *  - attach tslog-native transports directly (the OTLP/SigNoz http transport);
 *  - expose namespaced sub-loggers, level control, and flush.
 */
import { Logger } from 'tslog';
import { isLevelEnabled, levelValue } from '../level.js';
const EMIT_LEVELS = new Set(['silly', 'trace', 'debug', 'info', 'warn', 'error', 'fatal']);
const RESERVED_KEYS = new Set(['message', 'level', 'time', 'error', '_logMeta']);
/** tslog `minLevel` id: shared names map 1:1; `silent` sits above FATAL so nothing emits. */
function toTslogMinLevel(level) {
    return level === 'silent' ? 100 : levelValue(level);
}
function normalizeError(value) {
    if (value === null || typeof value !== 'object')
        return undefined;
    const record = value;
    const name = typeof record['name'] === 'string' ? record['name'] : 'Error';
    const message = typeof record['message'] === 'string' ? record['message'] : '';
    if (!('name' in record) && !('message' in record) && !('stack' in record))
        return undefined;
    const normalized = { name, message };
    if (typeof record['stack'] === 'string')
        normalized.stack = record['stack'];
    const cause = normalizeError(record['cause']);
    if (cause)
        normalized.cause = cause;
    return normalized;
}
/** Parse a tslog `format: "json"` line (known key shape) into the engine-neutral {@link LogRecord}. */
function parseRecord(line) {
    let parsed;
    try {
        parsed = JSON.parse(line);
    }
    catch {
        return undefined;
    }
    const meta = parsed['_logMeta'] ?? {};
    const rawLevel = String(parsed['level'] ?? meta['logLevelName'] ?? 'info').toLowerCase();
    const level = (EMIT_LEVELS.has(rawLevel) ? rawLevel : 'info');
    const parentNames = Array.isArray(meta['parentNames']) ? meta['parentNames'] : [];
    const name = typeof meta['name'] === 'string' ? [meta['name']] : [];
    const namespace = [...parentNames, ...name].filter(Boolean).join(':') || 'app';
    const context = {};
    for (const [key, value] of Object.entries(parsed)) {
        if (!RESERVED_KEYS.has(key))
            context[key] = value;
    }
    const timeRaw = parsed['time'] ?? meta['date'];
    const timestamp = typeof timeRaw === 'string' && !Number.isNaN(Date.parse(timeRaw)) ? Date.parse(timeRaw) : Date.now();
    const record = {
        level,
        levelValue: levelValue(level),
        namespace,
        message: typeof parsed['message'] === 'string' ? parsed['message'] : String(parsed['message'] ?? ''),
        timestamp,
    };
    if (Object.keys(context).length > 0)
        record.context = context;
    const error = normalizeError(parsed['error']);
    if (error)
        record.error = error;
    return record;
}
export function createEngine(config) {
    const facadeTransports = [];
    let rootLevel = config.level;
    const root = new Logger({
        name: config.namespace,
        type: config.prettyConsole ? 'pretty' : 'hidden',
        minLevel: toTslogMinLevel(config.level),
        mask: {
            keys: config.mask.keys,
            caseInsensitive: config.mask.caseInsensitive,
            placeholder: config.mask.placeholder,
            ...(config.mask.regex ? { regex: config.mask.regex } : {}),
        },
        json: { messageKey: 'message', levelKey: 'level', timeKey: 'time', errorKey: 'error' },
    });
    // Single fan-out: parse each emitted record once, dispatch to every facade transport that its
    // level clears. A throwing/rejecting transport is isolated so it can never break logging.
    root.attachTransport({
        name: 'facade-fanout',
        format: 'json',
        write: (_record, line) => {
            const record = parseRecord(line);
            if (!record)
                return;
            for (const transport of facadeTransports) {
                const floor = transport.minLevel ?? rootLevel;
                if (floor !== 'silent' && isLevelEnabled(record.level, floor)) {
                    try {
                        void Promise.resolve(transport.write(record)).catch(() => undefined);
                    }
                    catch {
                        // isolated — a transport must never break logging
                    }
                }
            }
        },
    });
    return {
        getSubLogger: (namespace, minLevel) => root.getSubLogger(minLevel ? { name: namespace, minLevel: toTslogMinLevel(minLevel) } : { name: namespace }),
        addFacadeTransport: (transport) => {
            facadeTransports.push(transport);
        },
        attachNative: (transport) => {
            root.attachTransport(transport);
        },
        use: (middleware) => {
            root.use(middleware);
        },
        setMinLevel: (level) => {
            rootLevel = level;
            root.setMinLevel(toTslogMinLevel(level));
        },
        flush: () => root.flush(),
    };
}
